import type { FC, ReactNode } from 'react'
import type { GetServerSideProps } from 'next'
import type { Hub, Post } from '@/types'

import Link from 'next/link'

import {
    ArrowRight,
    BookOpen,
    MessageSquareOff,
    RefreshCw,
    ShieldCheck,
    Stethoscope
} from 'lucide-react'

import { Seo } from '@/components/generals/seo'
import { Layout } from '@/components/generals/layout'
import { Container } from '@/components/generals/container'
import { JsonLd } from '@/components/generals/json-ld'
import { FaqSection } from '@/components/generals/faq-section'
import { Badge } from '@/components/ui/badge'
import { HubCard } from '@/components/blog/hub-card'
import { FeaturedPost } from '@/components/blog/featured-post'
import { PostCard } from '@/components/blog/post-card'
import { Newsletter } from '@/components/blog/newsletter'

import { getFeaturedPost, getHubs, getPosts } from '@/lib/db'
import { faqSchema, graph, itemListSchema } from '@/lib/structured-data'
import { HOME_FAQS } from '@/constants/faq'

interface HomeProps {
    hubs: Hub[]
    featured: Post | null
    latestPosts: Post[]
}

const VALUES = [
    {
        icon: Stethoscope,
        title: 'Evidence-based',
        description: 'Every guide is grounded in current dental research and clinical guidelines.'
    },
    {
        icon: BookOpen,
        title: 'Plain English',
        description: 'Complex dentistry explained without the jargon or the upsell.'
    },
    {
        icon: MessageSquareOff,
        title: 'No fear-mongering',
        description: 'Balanced, evidence-based information: just the facts you need.'
    },
    {
        icon: RefreshCw,
        title: 'Kept current',
        description: 'Articles are updated as clinical guidance evolves over time.'
    }
]

const Home: FC<HomeProps> = ({ hubs, featured, latestPosts }): ReactNode => {
    return (
        <Layout>
            <Seo path='/' />

            <JsonLd
                data={graph([
                    faqSchema(HOME_FAQS),
                    itemListSchema(
                        'Dental topic hubs',
                        hubs.map(hub => ({ name: hub.name, path: `/${hub.slug}` }))
                    )
                ])}
            />

            {/* Hero */}
            <section className='border-b border-border'>
                <Container className='grid items-center gap-12 py-16 lg:grid-cols-2 lg:py-24'>
                    <div>
                        <Badge variant='primary'>
                            <ShieldCheck className='size-3.5' />
                            Evidence-based dental knowledge
                        </Badge>

                        <h1 className='mt-5 font-heading text-4xl font-extrabold leading-[1.08] tracking-tight text-foreground sm:text-5xl'>
                            Your trusted guide to a healthier, brighter smile.
                        </h1>

                        <p className='mt-5 max-w-xl text-lg leading-relaxed text-muted-foreground'>
                            Smile Design Secret turns complex dentistry into clear, reliable guidance
                            across cosmetic dentistry, everyday dental health, and general care. Written for
                            patients, grounded in current dental research.
                        </p>

                        <div className='mt-8 flex flex-wrap items-center gap-3'>
                            <Link
                                href='#hubs'
                                className='inline-flex items-center justify-center gap-1.5 rounded-md bg-primary px-5 py-3 text-sm font-semibold text-primary-foreground transition-colors hover:bg-primary/90'
                            >
                                Explore the topics
                                <ArrowRight className='size-4' />
                            </Link>

                            <Link
                                href='#latest'
                                className='inline-flex items-center justify-center rounded-md border border-border bg-card px-5 py-3 text-sm font-semibold text-foreground transition-colors hover:border-primary/50 hover:text-primary'
                            >
                                Read the latest
                            </Link>
                        </div>

                        <dl className='mt-10 flex flex-wrap gap-x-10 gap-y-4'>
                            <div>
                                <dt className='text-2xl font-bold text-foreground'>3</dt>
                                <dd className='text-sm text-muted-foreground'>Focused topic hubs</dd>
                            </div>
                            <div>
                                <dt className='text-2xl font-bold text-foreground'>100%</dt>
                                <dd className='text-sm text-muted-foreground'>Evidence-based</dd>
                            </div>
                            <div>
                                <dt className='text-2xl font-bold text-foreground'>Growing</dt>
                                <dd className='text-sm text-muted-foreground'>Library of guides</dd>
                            </div>
                        </dl>
                    </div>

                    <div className='relative'>
                        <div className='overflow-hidden rounded-2xl border border-border'>
                            <img
                                src='/images/hero-smile.jpg'
                                alt='A person with a healthy, confident smile'
                                loading='eager'
                                decoding='async'
                                className='aspect-[4/5] w-full object-cover sm:aspect-[5/4] lg:aspect-[4/5]'
                            />
                        </div>

                        <div className='absolute bottom-4 left-4 flex items-center gap-3 rounded-xl border border-border bg-card/95 px-4 py-3 backdrop-blur'>
                            <span className='grid size-9 place-items-center rounded-lg bg-primary/10 text-primary'>
                                <ShieldCheck className='size-5' />
                            </span>

                            <div className='text-xs leading-tight'>
                                <div className='font-semibold text-foreground'>Evidence-based</div>
                                <div className='text-muted-foreground'>Grounded in dental research</div>
                            </div>
                        </div>
                    </div>
                </Container>
            </section>

            {/* Value strip */}
            <section className='border-b border-border bg-secondary/40'>
                <Container className='grid gap-8 py-12 sm:grid-cols-2 lg:grid-cols-4'>
                    {VALUES.map(value => (
                        <div key={value.title} className='flex gap-3.5'>
                            <span className='grid size-10 shrink-0 place-items-center rounded-lg bg-primary/10 text-primary'>
                                <value.icon className='size-5' />
                            </span>

                            <div>
                                <h3 className='text-sm font-semibold text-foreground'>{value.title}</h3>
                                <p className='mt-1 text-sm leading-relaxed text-muted-foreground'>
                                    {value.description}
                                </p>
                            </div>
                        </div>
                    ))}
                </Container>
            </section>

            {/* Hubs */}
            <section id='hubs' className='scroll-mt-20'>
                <Container className='py-16 lg:py-20'>
                    <div className='max-w-2xl'>
                        <span className='text-xs font-semibold uppercase tracking-wider text-primary'>
                            Topic hubs
                        </span>
                        <h2 className='mt-2 font-heading text-3xl font-bold tracking-tight text-foreground'>
                            Explore dentistry by topic
                        </h2>
                        <p className='mt-3 text-[15px] leading-relaxed text-muted-foreground'>
                            Three focused hubs, each a growing library of guides, so you can go straight to
                            what matters for your smile.
                        </p>
                    </div>

                    <div className='mt-10 grid gap-6 md:grid-cols-2 lg:grid-cols-3'>
                        {hubs.map(hub => (
                            <HubCard key={hub.slug} hub={hub} />
                        ))}
                    </div>
                </Container>
            </section>

            {/* Latest */}
            <section id='latest' className='scroll-mt-20 border-t border-border bg-secondary/30'>
                <Container className='py-16 lg:py-20'>
                    <div className='flex flex-wrap items-end justify-between gap-4'>
                        <div className='max-w-2xl'>
                            <span className='text-xs font-semibold uppercase tracking-wider text-primary'>
                                From the journal
                            </span>
                            <h2 className='mt-2 font-heading text-3xl font-bold tracking-tight text-foreground'>
                                Latest articles
                            </h2>
                        </div>
                    </div>

                    {featured && (
                        <div className='mt-10'>
                            <FeaturedPost post={featured} eyebrow='Editor’s pick' />
                        </div>
                    )}

                    {latestPosts.length > 0 && (
                        <div className='mt-8 grid gap-6 sm:grid-cols-2 lg:grid-cols-3'>
                            {latestPosts.map(post => (
                                <PostCard key={post.slug} post={post} />
                            ))}
                        </div>
                    )}
                </Container>
            </section>

            {/* FAQ */}
            <FaqSection
                items={HOME_FAQS}
                description='Everything you might want to know about how we work and what you will find here.'
            />

            {/* Newsletter */}
            <div className='py-16 lg:py-20'>
                <Newsletter />
            </div>
        </Layout>
    )
}

export const getServerSideProps: GetServerSideProps<HomeProps> = async () => {
    const [hubs, featured] = await Promise.all([getHubs(), getFeaturedPost()])

    const latestPosts = await getPosts({ limit: 6, excludeSlug: featured?.slug })

    return {
        props: {
            hubs,
            featured: featured ?? null,
            latestPosts
        }
    }
}

export default Home
